/*
 * BGK Independent Storefront Runtime
 * ---------------------------------
 * This file replaces the remote theme runtime with local storefront behavior
 * built on standard browser APIs and Shopify's public storefront/cart routes.
 * It intentionally contains no theme-license/authentication logic and makes
 * no requests to third-party theme services.
 */

(() => {
  'use strict';

  const BGK = (window.BGK = window.BGK || {});

  /* ---------- shared utilities used by other theme assets ---------- */
  function getFocusableElements(container) {
    return Array.from(
      container.querySelectorAll(
        'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type="hidden"]):enabled, select:enabled, textarea:enabled, object, iframe'
      )
    );
  }

  let activeTrap = null;
  function trapFocus(container, elementToFocus = container) {
    removeTrapFocus();
    const focusable = getFocusableElements(container);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    activeTrap = { container, first, last };

    container.addEventListener('keydown', focusTrapHandler);
    document.addEventListener('focusin', focusInTrapHandler);
    (elementToFocus || first).focus({ preventScroll: true });
  }

  function focusTrapHandler(event) {
    if (!activeTrap || event.key !== 'Tab') return;
    if (event.shiftKey && document.activeElement === activeTrap.first) {
      event.preventDefault();
      activeTrap.last.focus();
    } else if (!event.shiftKey && document.activeElement === activeTrap.last) {
      event.preventDefault();
      activeTrap.first.focus();
    }
  }

  function focusInTrapHandler(event) {
    if (!activeTrap || activeTrap.container.contains(event.target)) return;
    activeTrap.first.focus();
  }

  function removeTrapFocus(elementToFocus = null) {
    if (activeTrap) {
      activeTrap.container.removeEventListener('keydown', focusTrapHandler);
      document.removeEventListener('focusin', focusInTrapHandler);
      activeTrap = null;
    }
    if (elementToFocus && typeof elementToFocus.focus === 'function') {
      elementToFocus.focus({ preventScroll: true });
    }
  }

  function pauseAllMedia() {
    document.querySelectorAll('.js-youtube').forEach((video) => {
      if (video.contentWindow) video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    });
    document.querySelectorAll('.js-vimeo').forEach((video) => {
      if (video.contentWindow) video.contentWindow.postMessage('{"method":"pause"}', '*');
    });
    document.querySelectorAll('video').forEach((video) => video.pause());
    document.querySelectorAll('product-model').forEach((model) => {
      if (model.modelViewerUI && model.modelViewerUI.pause) model.modelViewerUI.pause();
    });
  }

  function debounce(fn, wait = 300) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: {
        Accept: `application/${type}`,
        'Content-Type': `application/${type}`
      }
    };
  }

  window.getFocusableElements = getFocusableElements;
  window.trapFocus = trapFocus;
  window.removeTrapFocus = removeTrapFocus;
  window.pauseAllMedia = pauseAllMedia;
  window.debounce = debounce;
  window.fetchConfig = fetchConfig;

  /* ---------- small event bus ---------- */
  const subscribers = {};
  const PUB_SUB_EVENTS = (window.PUB_SUB_EVENTS = {
    cartUpdate: 'cart-update',
    quantityUpdate: 'quantity-update',
    optionValueSelectionChange: 'option-value-selection-change',
    variantChange: 'variant-change',
    cartError: 'cart-error'
  });

  function subscribe(eventName, callback) {
    subscribers[eventName] = subscribers[eventName] || [];
    subscribers[eventName].push(callback);
    return () => {
      subscribers[eventName] = (subscribers[eventName] || []).filter((cb) => cb !== callback);
    };
  }

  function publish(eventName, payload) {
    (subscribers[eventName] || []).forEach((callback) => {
      try { callback(payload); } catch (error) { console.error(error); }
    });
  }

  window.subscribe = subscribe;
  window.publish = publish;

  /* ---------- helpers ---------- */
  const route = (name, fallback) => (window.routes && window.routes[name]) || fallback;
  const cartAddUrl = () => route('cart_add_url', '/cart/add.js');
  const cartChangeUrl = () => route('cart_change_url', '/cart/change.js');
  const cartUpdateUrl = () => route('cart_update_url', '/cart/update.js');
  const cartUrl = () => route('cart_url', '/cart');

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function moneyFromCents(cents, format) {
    const value = Number(cents || 0) / 100;
    if (format && format.includes('{{')) {
      const amount = value.toFixed(2);
      return format
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(value).toString())
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    }
    try {
      const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(value);
    } catch (_) {
      return value.toFixed(2);
    }
  }

  function sectionRoot(sectionId) {
    return document.getElementById(`MainProduct-${sectionId}`) || document.querySelector(`[data-section="${CSS.escape(sectionId)}"]`);
  }

  function getMainProductForm(sectionId) {
    const root = sectionRoot(sectionId) || document;
    return root.querySelector('product-form.main-product-form, product-form[data-main="true"]');
  }

  function getPrimaryVariantSelector(sectionId) {
    return document.querySelector(`variant-selects[data-section="${CSS.escape(sectionId)}"]`);
  }

  function setButtonState(sectionId, variant) {
    const available = !!(variant && variant.available);
    const soldOutText = (window.variantStrings && window.variantStrings.soldOut) || 'Sold out';
    const addText = (window.variantStrings && window.variantStrings.addToCart) || 'Add to cart';

    document.querySelectorAll(`#ProductForm-${CSS.escape(sectionId)} [name="add"], #sticky-atc-${CSS.escape(sectionId)} button[id^="SectionAtcBtn-"]`).forEach((button) => {
      button.disabled = !available;
      if (!available) button.setAttribute('data-unavailable', 'true');
      else button.removeAttribute('data-unavailable');
      const label = button.querySelector('.main-atc__label__text, .sticky-atc__label__text, .button__label');
      if (label && !label.querySelector('*')) label.textContent = available ? addText : soldOutText;
      else {
        const textTarget = button.querySelector('.main-atc__label__text, .sticky-atc__label__text');
        if (textTarget) textTarget.textContent = available ? addText : soldOutText;
      }
    });
  }

  function updateQuantityRule(sectionId, variant) {
    if (!variant || !variant.quantity_rule) return;
    const input = document.getElementById(`Quantity-${sectionId}`);
    if (!input) return;
    const rule = variant.quantity_rule;
    const min = Number(rule.min || 1);
    const step = Number(rule.increment || 1);
    input.min = String(min);
    input.dataset.min = String(min);
    input.step = String(step);
    if (rule.max != null) {
      input.max = String(rule.max);
      input.dataset.max = String(rule.max);
    } else {
      input.removeAttribute('max');
      delete input.dataset.max;
    }
    if (Number(input.value) < min) input.value = String(min);
  }

  async function fetchProductSection(productUrl, sectionId, variantId) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', String(variantId));
    url.searchParams.set('section_id', sectionId);
    const response = await fetch(url.toString(), { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Unable to refresh product section (${response.status})`);
    return parseHTML(await response.text());
  }

  function replaceInnerFromDocument(id, doc) {
    const current = document.getElementById(id);
    const fresh = doc.getElementById(id);
    if (current && fresh) current.innerHTML = fresh.innerHTML;
  }

  function syncServerRenderedProductFragments(sectionId, doc) {
    [
      `price-${sectionId}`,
      `Inventory-${sectionId}`,
      `Sku-${sectionId}`,
      `sticky-atc-separate-price-${sectionId}`,
      `sticky-atc-price-${sectionId}`
    ].forEach((id) => replaceInnerFromDocument(id, doc));
  }

  function updateVariantInputs(sectionId, variantId) {
    const root = sectionRoot(sectionId) || document;
    root.querySelectorAll('product-form.main-product-form input[name="id"], product-form[data-main="true"] input[name="id"]').forEach((input) => {
      input.value = String(variantId);
      input.dispatchEvent(new Event('change', { bubbles: false }));
    });
  }

  function updateFeaturedMedia(sectionId, variant) {
    if (!variant || !variant.featured_media) return;
    const gallery = document.getElementById(`MediaGallery-${sectionId}`);
    if (gallery && typeof gallery.setActiveMedia === 'function') {
      try { gallery.setActiveMedia(`${sectionId}-${variant.featured_media.id}`, true, false, variant); } catch (_) {}
    }
    const stickyImage = document.getElementById(`sticky-atc-image-${sectionId}`);
    const preview = variant.featured_media.preview_image || variant.featured_media;
    if (stickyImage && preview && preview.src) stickyImage.src = preview.src;
  }

  function syncStickyVariantSelectors(sectionId, variant) {
    if (!variant) return;
    document.querySelectorAll(`secondary-variant-select[data-section="${CSS.escape(sectionId)}"], secondary-variant-select-separate[data-section="${CSS.escape(sectionId)}"]`).forEach((element) => {
      if (typeof element.syncToVariant === 'function') element.syncToVariant(variant);
    });
  }

  function syncQuantityBreakSelectors(sectionId, variant) {
    if (!variant) return;
    const qb = document.querySelector(`quantity-breaks[data-section="${CSS.escape(sectionId)}"]`);
    if (!qb) return;
    qb.querySelectorAll('.quantity-break__selector-item').forEach((item) => {
      item.dataset.selectedId = String(variant.id);
      item.querySelectorAll('select.quantity-break__variant-select').forEach((select, index) => {
        if (variant.options && variant.options[index] != null) select.value = variant.options[index];
      });
    });
  }

  function updatePickup(sectionId, variant) {
    const pickup = document.querySelector(`#ProductInfo-${CSS.escape(sectionId)} pickup-availability`);
    if (!pickup || !variant) return;
    pickup.dataset.variantId = String(variant.id);
    if (typeof pickup.fetchAvailability === 'function') pickup.fetchAvailability(variant.id);
  }

  async function applyVariant(sectionId, productUrl, variant, options = {}) {
    if (!variant) {
      setButtonState(sectionId, null);
      return;
    }
    updateVariantInputs(sectionId, variant.id);
    updateQuantityRule(sectionId, variant);
    setButtonState(sectionId, variant);
    updateFeaturedMedia(sectionId, variant);
    syncStickyVariantSelectors(sectionId, variant);
    syncQuantityBreakSelectors(sectionId, variant);
    updatePickup(sectionId, variant);

    if (options.updateUrl !== false && productUrl) {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }

    const detail = { sectionId, variant, options: variant.options || [] };
    document.dispatchEvent(new CustomEvent('bgk:variant-change', { detail }));
    publish(PUB_SUB_EVENTS.variantChange, { data: detail });

    try {
      const doc = await fetchProductSection(productUrl, sectionId, variant.id);
      syncServerRenderedProductFragments(sectionId, doc);
    } catch (error) {
      console.warn('[BGK] Variant fragment refresh skipped:', error.message);
    }
  }

  BGK.applyVariant = applyVariant;

  /* ---------- quantity ---------- */
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.querySelectorAll('button').forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)));
      this.input.addEventListener('change', () => this.publishChange());
    }

    onButtonClick(event) {
      event.preventDefault();
      const previous = Number(this.input.value || 0);
      const step = Number(this.input.step || 1);
      const min = Number(this.input.min || this.input.dataset.min || 0);
      const max = this.input.max ? Number(this.input.max) : Infinity;
      const next = event.currentTarget.name === 'plus' ? previous + step : previous - step;
      this.input.value = String(Math.min(max, Math.max(min, next)));
      if (Number(this.input.value) !== previous) this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    publishChange() {
      const quantity = Number(this.input.value || 0);
      document.dispatchEvent(new CustomEvent('bgk:quantity-change', { detail: { quantity, sectionId: this.dataset.section || null } }));
      publish(PUB_SUB_EVENTS.quantityUpdate, { quantity });
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---------- quantity breaks ---------- */
  class QuantityBreaks extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      this.updateSelection();
    }

    onClick(event) {
      const card = event.target.closest('.quantity-break');
      if (!card) return;
      const radio = card.previousElementSibling && card.previousElementSibling.matches('input[type="radio"][name="quantity"]')
        ? card.previousElementSibling
        : this.querySelector(`#${CSS.escape(card.getAttribute('for') || '')}`);
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    onChange(event) {
      if (event.target.matches('input[type="radio"][name="quantity"]')) {
        this.updateSelection();
        const quantity = Number(event.target.dataset.quantity || event.target.value || 1);
        event.target.value = String(quantity);
        document.dispatchEvent(new CustomEvent('bgk:quantity-change', { detail: { quantity, sectionId: this.sectionId } }));
      }
      if (event.target.matches('select.quantity-break__variant-select')) this.requestVariantFromSelectors(event.target);
    }

    updateSelection() {
      const checked = this.querySelector('input[type="radio"][name="quantity"]:checked');
      this.querySelectorAll('.quantity-break').forEach((card) => card.classList.remove('quantity-break--selected'));
      if (checked) {
        const label = this.querySelector(`label[for="${CSS.escape(checked.id)}"]`);
        if (label) label.classList.add('quantity-break--selected');
        const quantity = Number(checked.dataset.quantity || checked.value || 1);
        checked.value = String(quantity);
      }
    }

    requestVariantFromSelectors(changedSelect) {
      const item = changedSelect.closest('.quantity-break__selector-item');
      if (!item) return;
      const options = Array.from(item.querySelectorAll('select.quantity-break__variant-select')).map((select) => select.value);
      const main = getPrimaryVariantSelector(this.sectionId);
      if (main && typeof main.selectOptions === 'function') main.selectOptions(options);
    }
  }
  if (!customElements.get('quantity-breaks')) customElements.define('quantity-breaks', QuantityBreaks);

  /* ---------- visual gift unlocks ---------- */
  class QuantityGifts extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this.boundUpdate = this.update.bind(this);
      document.addEventListener('bgk:quantity-change', this.boundUpdate);
      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('bgk:quantity-change', this.boundUpdate);
    }

    selectedQuantity() {
      const root = sectionRoot(this.sectionId) || document;
      const checkedBreak = root.querySelector('quantity-breaks input[name="quantity"]:checked');
      if (checkedBreak) return Number(checkedBreak.dataset.quantity || checkedBreak.value || 1);
      const input = root.querySelector('input[name="quantity"]');
      return Number(input ? input.value : 1);
    }

    update(event) {
      if (event && event.detail && event.detail.sectionId && event.detail.sectionId !== this.sectionId) return;
      const quantity = this.selectedQuantity();
      this.querySelectorAll('.quantity-gift').forEach((gift) => {
        const unlocked = quantity >= Number(gift.dataset.quantity || 1);
        gift.dataset.unlocked = String(unlocked);
        gift.classList.toggle('quantity-gift--unlocked', unlocked);
      });
    }
  }
  if (!customElements.get('quantity-gifts')) customElements.define('quantity-gifts', QuantityGifts);

  /* ---------- product custom fields ---------- */
  class CustomProductField extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this.fieldName = this.dataset.name;
      this.required = this.dataset.required === 'true';
      this.control = this.querySelector('input:not([type="radio"]), textarea, select') || this.querySelector('input[type="radio"]');
      this.installHiddenField();
      this.addEventListener('input', () => this.sync());
      this.addEventListener('change', () => this.sync());
      this.sync();
    }

    getValue() {
      if (this.dataset.type === 'pills') {
        const checked = this.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : '';
      }
      const control = this.querySelector('input:not([type="radio"]), textarea, select');
      return control ? control.value : (this.dataset.defaultValue || '');
    }

    installHiddenField() {
      const productForm = getMainProductForm(this.sectionId);
      const form = productForm && productForm.querySelector('form');
      if (!form || !this.fieldName) return;
      const name = `properties[${this.fieldName}]`;
      let hidden = form.querySelector(`input[type="hidden"][name="${CSS.escape(name)}"][data-bgk-custom-field]`);
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = name;
        hidden.dataset.bgkCustomField = this.id || this.fieldName;
        form.appendChild(hidden);
      }
      this.hiddenInput = hidden;
    }

    sync() {
      if (!this.hiddenInput) this.installHiddenField();
      const value = this.getValue();
      if (this.hiddenInput) this.hiddenInput.value = value;
      const error = this.querySelector('.input-error-msg');
      if (error) error.style.display = 'none';
    }

    validate() {
      const value = String(this.getValue() || '').trim();
      const valid = !this.required || value.length > 0;
      const error = this.querySelector('.input-error-msg');
      if (error) error.style.display = valid ? 'none' : '';
      if (!valid) {
        const control = this.querySelector('input, textarea, select');
        if (control) control.focus();
      }
      return valid;
    }
  }
  if (!customElements.get('custom-product-field')) customElements.define('custom-product-field', CustomProductField);

  /* ---------- add-on products ---------- */
  class ProductInfoUpsell extends HTMLElement {
    constructor() {
      super();
      this.toggleButton = this.querySelector('.upsell-toggle-btn');
      if (this.toggleButton) this.toggleButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.dataset.selected = this.dataset.selected === 'true' ? 'false' : 'true';
      });
      if (this.dataset.toggle === 'true' && this.dataset.style !== 'add_button') {
        this.addEventListener('click', (event) => {
          if (event.target.closest('select, a, button, input')) return;
          this.dataset.selected = this.dataset.selected === 'true' ? 'false' : 'true';
        });
      }
      this.querySelectorAll('select.variant-dropdown').forEach((select) => select.addEventListener('change', this.onVariantChange.bind(this)));
      this.onVariantChange();
    }

    variants() {
      const script = this.querySelector('.upsell__variant-picker script[type="application/json"], script[type="application/json"]');
      if (!script) return [];
      try { return JSON.parse(script.textContent); } catch (_) { return []; }
    }

    onVariantChange() {
      const selects = Array.from(this.querySelectorAll('.upsell__variant-picker select.variant-dropdown'));
      if (!selects.length) return;
      const options = selects.map((select) => select.value);
      const variant = this.variants().find((candidate) => candidate.options && candidate.options.every((value, index) => value === options[index]));
      if (!variant) return;
      this.dataset.id = String(variant.id);
      const hidden = this.querySelector('product-form input[name="id"]');
      if (hidden) hidden.value = String(variant.id);
    }
  }
  if (!customElements.get('product-info-upsell')) customElements.define('product-info-upsell', ProductInfoUpsell);

  class CartDrawerUpsell extends ProductInfoUpsell {}
  if (!customElements.get('cart-drawer-upsell')) customElements.define('cart-drawer-upsell', CartDrawerUpsell);
  class CartDrawerGift extends CartDrawerUpsell {}
  if (!customElements.get('cart-drawer-gift')) customElements.define('cart-drawer-gift', CartDrawerGift);

  /* ---------- variants ---------- */
  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url || window.location.pathname;
      this.variantData = this.getVariantData();
      this.addEventListener('change', this.onVariantChange.bind(this));
    }

    getVariantData() {
      const script = this.querySelector('script[type="application/json"]');
      if (!script) return [];
      try { return JSON.parse(script.textContent); } catch (_) { return []; }
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('.product-form__input')).map((group) => {
        const select = group.querySelector('select');
        if (select) return select.value;
        const checked = group.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      }).filter((value) => value !== null);
    }

    currentVariant(options = this.selectedOptions()) {
      return this.variantData.find((variant) => variant.options && variant.options.every((value, index) => value === options[index]));
    }

    async onVariantChange() {
      const options = this.selectedOptions();
      const variant = this.currentVariant(options);
      this.updateCustomLabels(options);
      if (!variant) {
        setButtonState(this.sectionId, null);
        return;
      }
      await applyVariant(this.sectionId, this.productUrl, variant, { updateUrl: this.dataset.updateUrl !== 'false', source: this });
    }

    updateCustomLabels(options) {
      options.forEach((value, index) => {
        const label = document.getElementById(`custom-label-${this.sectionId}-${index + 1}`);
        if (!label) return;
        const current = label.textContent;
        const marker = current.includes(' - ') ? current.split(' - ')[0] : null;
        if (marker) label.textContent = `${marker} - ${value}`;
      });
    }

    selectOptions(options) {
      if (!Array.isArray(options)) return;
      Array.from(this.querySelectorAll('.product-form__input')).forEach((group, index) => {
        if (options[index] == null) return;
        const select = group.querySelector('select');
        if (select) select.value = options[index];
        group.querySelectorAll('input[type="radio"]').forEach((input) => { input.checked = input.value === options[index]; });
      });
      this.onVariantChange();
    }
  }
  if (!customElements.get('variant-selects')) customElements.define('variant-selects', VariantSelects);

  class SecondaryVariantSelect extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this.variantData = this.getVariantData();
      this.addEventListener('change', this.onChange.bind(this));
    }

    getVariantData() {
      const script = this.querySelector('script[type="application/json"]');
      if (!script) return [];
      try { return JSON.parse(script.textContent); } catch (_) { return []; }
    }

    onChange() {
      let options = [];
      const combined = this.querySelector('select.sticky-atc__variant-select');
      if (combined) options = (combined.selectedOptions[0] && combined.selectedOptions[0].dataset.options ? combined.selectedOptions[0].dataset.options : combined.value).split(',');
      else options = Array.from(this.querySelectorAll('select')).map((select) => select.value);
      const main = getPrimaryVariantSelector(this.sectionId);
      if (main && typeof main.selectOptions === 'function') main.selectOptions(options);
      else {
        const variant = this.variantData.find((candidate) => candidate.options && candidate.options.every((value, index) => value === options[index]));
        if (variant) applyVariant(this.sectionId, this.dataset.url || window.location.pathname, variant, { updateUrl: true, source: this });
      }
    }

    syncToVariant(variant) {
      const combined = this.querySelector('select.sticky-atc__variant-select');
      if (combined) {
        const option = Array.from(combined.options).find((item) => item.dataset.options === variant.options.join(',') || item.value === variant.options.join(','));
        if (option) combined.value = option.value;
      } else {
        Array.from(this.querySelectorAll('select')).forEach((select, index) => { if (variant.options[index] != null) select.value = variant.options[index]; });
      }
    }
  }
  if (!customElements.get('secondary-variant-select')) customElements.define('secondary-variant-select', SecondaryVariantSelect);
  class SecondaryVariantSelectSeparate extends SecondaryVariantSelect {}
  if (!customElements.get('secondary-variant-select-separate')) customElements.define('secondary-variant-select-separate', SecondaryVariantSelectSeparate);

  /* ---------- product information shell ---------- */
  class ProductInfo extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
    }
    initShareLinks() {}
  }
  if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

  /* ---------- product form ---------- */
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[type="submit"][name="add"]');
      if (this.form) this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    validateCustomFields() {
      const root = this.closest('product-info') || this.closest('[id^="MainProduct-"]') || document;
      return Array.from(root.querySelectorAll('custom-product-field')).every((field) => typeof field.validate !== 'function' || field.validate());
    }

    getProperties(formData) {
      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && String(value).length) properties[match[1]] = value;
      }
      return properties;
    }

    selectedUpsells() {
      if (!this.matches('.main-product-form') && this.dataset.main !== 'true') return [];
      const root = this.closest('product-info') || document;
      return Array.from(root.querySelectorAll('product-info-upsell[data-selected="true"]'))
        .map((upsell) => Number(upsell.dataset.id))
        .filter(Boolean)
        .map((id) => ({ id, quantity: 1 }));
    }

    async handleSubmit(event) {
      if (event) event.preventDefault();
      if (!this.form || this.submitting) return;
      if (!this.validateCustomFields()) return;
      this.submitting = true;

      const buttons = Array.from(this.querySelectorAll('.product-form__submit, [type="submit"][name="add"]'));
      buttons.forEach((button) => {
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        const spinner = button.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.remove('hidden');
      });

      const errorWrapper = this.querySelector('.product-form__error-message-wrapper');
      try {
        const formData = new FormData(this.form);
        const id = Number(formData.get('id'));
        const quantity = Number(formData.get('quantity') || 1);
        if (!id) throw new Error('Please select a product variant.');

        const baseItem = { id, quantity: Math.max(1, quantity) };
        const properties = this.getProperties(formData);
        if (Object.keys(properties).length) baseItem.properties = properties;
        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) baseItem.selling_plan = Number(sellingPlan);

        const items = [baseItem, ...this.selectedUpsells()];
        const response = await fetch(cartAddUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to add this item to the cart.');

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', cartData: payload });
        document.dispatchEvent(new CustomEvent('bgk:cart-updated', { detail: payload }));

        const skipCart = this.querySelector('input[name="id"][data-skip-cart="true"]') || (event && event.currentTarget && event.currentTarget.dataset && event.currentTarget.dataset.skipCartButton === 'true');
        if (skipCart) {
          window.location.href = '/checkout';
          return;
        }

        await refreshCartUI(true);
        if (errorWrapper) errorWrapper.hidden = true;
      } catch (error) {
        console.error('[BGK] Add to cart:', error);
        if (errorWrapper) {
          errorWrapper.hidden = false;
          const message = errorWrapper.querySelector('.product-form__error-message');
          if (message) message.textContent = error.message;
        }
        publish(PUB_SUB_EVENTS.cartError, { source: 'product-form', error });
      } finally {
        this.submitting = false;
        buttons.forEach((button) => {
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          const spinner = button.querySelector('.loading-overlay__spinner');
          if (spinner) spinner.classList.add('hidden');
        });
      }
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* ---------- cart drawer ---------- */
  async function getCartSections() {
    const url = new URL(cartUrl(), window.location.origin);
    url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
    const response = await fetch(url.toString(), { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Cart refresh failed (${response.status})`);
    return response.json();
  }

  async function refreshCartUI(openAfter = false) {
    const drawer = document.querySelector('cart-drawer');
    try {
      const sections = await getCartSections();
      if (drawer && sections['cart-drawer']) {
        const doc = parseHTML(sections['cart-drawer']);
        const fresh = doc.querySelector('cart-drawer');
        if (fresh) drawer.replaceContentFrom(fresh);
      }
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && sections['cart-icon-bubble']) {
        const doc = parseHTML(sections['cart-icon-bubble']);
        const freshBubble = doc.getElementById('cart-icon-bubble') || doc.querySelector('.shopify-section');
        if (freshBubble) bubble.innerHTML = freshBubble.id === 'cart-icon-bubble' ? freshBubble.innerHTML : freshBubble.innerHTML;
      }
      if (openAfter && drawer) drawer.open();
    } catch (error) {
      console.warn('[BGK] Cart section refresh fallback:', error.message);
      if (openAfter && drawer) drawer.open();
    }
  }
  BGK.refreshCartUI = refreshCartUI;

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.bindEvents();
    }

    bindEvents() {
      const overlay = this.querySelector('#CartDrawer-Overlay, .cart-drawer__overlay');
      if (overlay) overlay.onclick = () => this.close();
      this.querySelectorAll('[onclick*="closest(\'cart-drawer\').close"], .drawer__close').forEach((button) => {
        button.onclick = (event) => { event.preventDefault(); this.close(); };
      });
      this.onKeyUp = (event) => { if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close(); };
      this.addEventListener('keyup', this.onKeyUp);
    }

    replaceContentFrom(freshDrawer) {
      if (!freshDrawer) return;

      // The cart drawer is rendered with `is-empty` when the cart is empty.
      // After adding/removing items we must sync that class as well as the
      // rendered HTML; otherwise the theme CSS intentionally hides the
      // header/body/footer and the drawer appears as a blank white panel.
      const shouldRemainOpen = this.classList.contains('active') || freshDrawer.classList.contains('active');
      const nextClasses = Array.from(freshDrawer.classList).filter((name) => name !== 'active');

      this.className = nextClasses.join(' ');
      this.innerHTML = freshDrawer.innerHTML;

      if (freshDrawer.dataset.type) this.dataset.type = freshDrawer.dataset.type;
      else this.removeAttribute('data-type');

      this.bindEvents();

      if (shouldRemainOpen) this.open();
    }

    // Kept for compatibility with any other local component that passes only HTML.
    replaceContent(html) {
      const wasOpen = this.classList.contains('active');
      this.innerHTML = html;
      this.bindEvents();
      if (wasOpen) this.open();
    }

    open(triggeredBy = null) {
      this.activeElement = triggeredBy || document.activeElement;
      this.classList.add('active');
      document.body.classList.add('overflow-hidden');
      const inner = this.querySelector('.drawer__inner');
      if (inner) setTimeout(() => trapFocus(this, inner.querySelector('button, a, input, select') || inner), 50);
    }

    close() {
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      removeTrapFocus(this.activeElement);
    }

    async renderContents() {
      await refreshCartUI(true);
    }
  }
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  class CartItems extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('change', debounce(this.onChange.bind(this), 250));
    }

    onChange(event) {
      const input = event.target.closest('input[name="updates[]"], input[data-index]');
      if (!input || !input.dataset.index) return;
      this.updateQuantity(Number(input.dataset.index), Number(input.value));
    }

    async updateQuantity(line, quantity) {
      try {
        const response = await fetch(cartChangeUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || 'Unable to update cart.');
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: data });
        await refreshCartUI(this.closest('cart-drawer') != null);
      } catch (error) {
        console.error('[BGK] Cart update:', error);
      }
    }
  }
  if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);
  class CartDrawerItems extends CartItems {}
  if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);

  class CartRemoveButton extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (event) => {
        event.preventDefault();
        const items = this.closest('cart-drawer-items, cart-items');
        if (items && typeof items.updateQuantity === 'function') items.updateQuantity(Number(this.dataset.index), 0);
      });
    }
  }
  if (!customElements.get('cart-remove-button')) customElements.define('cart-remove-button', CartRemoveButton);

  class CartNote extends HTMLElement {
    constructor() {
      super();
      const field = this.querySelector('textarea, input');
      if (field) field.addEventListener('change', debounce(() => {
        fetch(cartUpdateUrl(), {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: field.value })
        }).catch(console.error);
      }, 300));
    }
  }
  if (!customElements.get('cart-note')) customElements.define('cart-note', CartNote);

  /* ---------- sticky add to cart ---------- */
  class StickyAtc extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      if (this.dataset.afterScroll === 'true') {
        this.observerTarget = document.getElementById(`ProductSubmitButton-${this.sectionId}`) || document.querySelector(`#ProductForm-${CSS.escape(this.sectionId)}`);
        if (this.observerTarget) {
          this.observer = new IntersectionObserver((entries) => {
            const visible = entries[0] && entries[0].isIntersecting;
            this.style.transform = visible ? 'translateY(100%)' : 'translateY(0)';
            document.documentElement.style.setProperty('--sticky-atc-height', visible ? '0px' : `${this.offsetHeight}px`);
            document.querySelectorAll('.scroll-to-top-btn, .music-player').forEach((el) => el.style.setProperty('--sticky-atc-offset', visible ? '0px' : `${this.offsetHeight}px`));
          }, { threshold: 0 });
          this.observer.observe(this.observerTarget);
        }
      } else {
        this.style.transform = 'translateY(0)';
      }
      if (this.dataset.scrollBtn === 'true') {
        const button = this.querySelector('button');
        if (button) button.addEventListener('click', (event) => {
          event.preventDefault();
          const target = document.querySelector(this.dataset.scrollDestination);
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    }
  }
  if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);

  /* ---------- dates used by shipping checkpoints ---------- */
  class DynamicDates extends HTMLElement {
    constructor() {
      super();
      this.renderDates();
    }

    renderDates() {
      const format = this.dataset.dateFormat || this.dataset.format || 'dd_mm';
      const dayLabels = (this.dataset.daysLabels || this.dataset.days || 'Mon,Tue,Wed,Thu,Fri,Sat,Sun').split(',').map((v) => v.trim());
      const monthLabels = (this.dataset.monthsLabels || this.dataset.months || 'Jan,Feb,Mar,Apr,May,Jun,Jul,Aug,Sep,Oct,Nov,Dec').split(',').map((v) => v.trim());
      const formatDate = (date) => {
        const dayName = dayLabels[(date.getDay() + 6) % 7] || '';
        const monthName = monthLabels[date.getMonth()] || String(date.getMonth() + 1);
        const day = date.getDate();
        if (format === 'mm_dd') return `${monthName} ${day}`;
        if (format === 'day_dd_mm') return `${dayName}, ${day} ${monthName}`;
        return `${day} ${monthName}`;
      };
      this.querySelectorAll('[data-dynamic-date="true"], [data-min-days], [data-start-days], [data-max-days]').forEach((node) => {
        const min = Number(node.dataset.minDays || node.dataset.startDays || 0);
        const max = Number(node.dataset.maxDays || min);
        const start = new Date(); start.setDate(start.getDate() + min);
        const end = new Date(); end.setDate(end.getDate() + max);
        const template = node.dataset.text || node.innerHTML;
        node.innerHTML = template
          .replace(/\[start_date\]/g, formatDate(start))
          .replace(/\[end_date\]/g, formatDate(end));
      });
    }
  }
  if (!customElements.get('dynamic-dates')) customElements.define('dynamic-dates', DynamicDates);

  /* ---------- search ---------- */
  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.resetButton = this.querySelector('button[type="reset"]');
      if (this.resetButton) this.resetButton.addEventListener('click', () => this.reset());
    }
    reset() {
      if (this.input) this.input.value = '';
      this.input && this.input.focus();
    }
  }
  if (!customElements.get('search-form')) customElements.define('search-form', SearchForm);

  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      this.results = this.querySelector('[data-predictive-search]') || this.querySelector('.predictive-search');
      if (this.input) this.input.addEventListener('input', debounce(this.onInput.bind(this), 250));
    }
    async onInput() {
      const term = this.input.value.trim();
      if (!term) return this.close();
      const url = new URL((window.routes && window.routes.predictive_search_url) || '/search/suggest', window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('section_id', 'predictive-search');
      try {
        const response = await fetch(url.toString(), { credentials: 'same-origin' });
        const doc = parseHTML(await response.text());
        const fresh = doc.getElementById('shopify-section-predictive-search') || doc.querySelector('#predictive-search-results');
        if (this.results && fresh) this.results.innerHTML = fresh.id === 'predictive-search-results' ? fresh.outerHTML : fresh.innerHTML;
        this.setAttribute('open', 'true');
        if (this.results) this.results.style.display = '';
      } catch (error) { console.warn('[BGK] Predictive search:', error.message); }
    }
    close() {
      this.removeAttribute('open');
      if (this.results) this.results.style.display = 'none';
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

  /* ---------- localization ---------- */
  class LocalizationForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.button = this.querySelector('button.localization-form__select');
      this.list = this.querySelector('.disclosure__list-wrapper');
      if (this.button) this.button.addEventListener('click', this.toggle.bind(this));
      this.querySelectorAll('a[data-value]').forEach((link) => link.addEventListener('click', this.onItemClick.bind(this)));
      document.addEventListener('click', (event) => { if (!this.contains(event.target)) this.close(); });
    }
    toggle() {
      if (!this.list) return;
      const opening = this.list.hidden;
      this.list.hidden = !opening;
      this.button.setAttribute('aria-expanded', String(opening));
    }
    close() {
      if (this.list) this.list.hidden = true;
      if (this.button) this.button.setAttribute('aria-expanded', 'false');
    }
    onItemClick(event) {
      event.preventDefault();
      const input = this.querySelector('input[type="hidden"]');
      if (input) input.value = event.currentTarget.dataset.value;
      if (this.form) this.form.submit();
    }
  }
  if (!customElements.get('localization-form')) customElements.define('localization-form', LocalizationForm);

  /* ---------- auxiliary storefront elements ---------- */
  class CardVariantSelects extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section || '';
      this.variants = this.getVariantData();
      this.addEventListener('change', this.onVariantChange.bind(this));
      this.onVariantChange();
    }

    getVariantData() {
      const script = this.querySelector('script[type="application/json"]');
      if (!script) return [];
      try { return JSON.parse(script.textContent); } catch (_) { return []; }
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('.product-form__input')).map((group) => {
        const checked = group.querySelector('input[type="radio"]:checked');
        const select = group.querySelector('select');
        return checked ? checked.value : (select ? select.value : null);
      }).filter((value) => value != null);
    }

    currentVariant() {
      const options = this.selectedOptions();
      return this.variants.find((variant) => variant.options && variant.options.every((value, index) => value === options[index]));
    }

    onVariantChange() {
      const variant = this.currentVariant();
      if (!variant) return;

      const form = document.getElementById(this.sectionId);
      if (form) {
        const input = form.querySelector('input[name="id"]');
        if (input) {
          input.disabled = false;
          input.value = String(variant.id);
          input.dispatchEvent(new Event('change', { bubbles: false }));
        }
        const button = form.querySelector('[type="submit"][name="add"]');
        if (button) button.disabled = !variant.available;
      }

      const card = this.closest('.card-wrapper, .card');
      const preview = variant.featured_media && (variant.featured_media.preview_image || variant.featured_media);
      if (card && preview && preview.src) {
        const images = card.querySelectorAll('.card__media img');
        if (images[0]) {
          images[0].src = preview.src;
          images[0].removeAttribute('srcset');
        }
      }

      this.dataset.variantId = String(variant.id);
      this.dispatchEvent(new CustomEvent('bgk:card-variant-change', { bubbles: true, detail: { variant } }));
    }
  }
  if (!customElements.get('card-variant-selects')) customElements.define('card-variant-selects', CardVariantSelects);

  class ModalV2 extends HTMLElement {
    constructor() {
      super();
      this.openButton = this.querySelector('.modal-v2__open-btn');
      this.closeButtons = this.querySelectorAll('.modal-v2__close-btn');
      if (this.openButton) this.openButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.open();
      });
      this.closeButtons.forEach((button) => button.addEventListener('click', (event) => {
        event.preventDefault();
        this.close();
      }));
      this.addEventListener('keyup', (event) => {
        if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
      });
      if (String(this.dataset.open) === 'true') this.open(false);
    }

    open(useFocus = true) {
      this.dataset.open = 'true';
      this.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
      const modal = this.querySelector('.modal-v2__modal');
      if (modal) {
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        if (!modal.hasAttribute('tabindex')) modal.setAttribute('tabindex', '-1');
        if (useFocus) setTimeout(() => trapFocus(this, modal), 20);
      }
    }

    close() {
      if (String(this.dataset.open) !== 'true' && !this.hasAttribute('open')) return;
      this.dataset.open = 'false';
      this.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      removeTrapFocus(this.openButton);
    }
  }
  if (!customElements.get('modal-v2')) customElements.define('modal-v2', ModalV2);

  /* ---------- small compatibility elements ---------- */
  class InternalVideo extends HTMLElement {
    constructor() {
      super();
      const video = this.querySelector('video');
      const play = this.querySelector('.internal-video__play');
      const sound = this.querySelector('.internal-video__sound-btn');
      if (play && video) play.addEventListener('click', () => video.paused ? video.play() : video.pause());
      if (sound && video) sound.addEventListener('click', () => { video.muted = !video.muted; });
    }
  }
  if (!customElements.get('internal-video')) customElements.define('internal-video', InternalVideo);

  class ClickableDiscount extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', async () => {
        const code = this.dataset.code || this.textContent.trim();
        if (!code) return;
        try { await navigator.clipboard.writeText(code); } catch (_) {}
      });
    }
  }
  if (!customElements.get('clickable-discount')) customElements.define('clickable-discount', ClickableDiscount);

  class ComparisonSlider extends HTMLElement {
    constructor() {
      super();
      const range = this.querySelector('input[type="range"]');
      if (range) range.addEventListener('input', () => this.style.setProperty('--position', `${range.value}%`));
    }
  }
  if (!customElements.get('comparison-slider')) customElements.define('comparison-slider', ComparisonSlider);

  class BundleDeals extends HTMLElement {}
  if (!customElements.get('bundle-deals')) customElements.define('bundle-deals', BundleDeals);
  class MainBundleOffer extends HTMLElement {}
  if (!customElements.get('main-bundle-offer')) customElements.define('main-bundle-offer', MainBundleOffer);
  class SectionGroup extends HTMLElement {}
  if (!customElements.get('section-group')) customElements.define('section-group', SectionGroup);
  class PromoPopup extends HTMLElement {}
  if (!customElements.get('promo-popup')) customElements.define('promo-popup', PromoPopup);

  /* ---------- global cart icon ---------- */
  function bindCartIcon() {
    const icon = document.getElementById('cart-icon-bubble');
    if (!icon || icon.dataset.bgkBound === 'true') return;
    icon.dataset.bgkBound = 'true';
    icon.addEventListener('click', (event) => {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      event.preventDefault();
      drawer.open(icon);
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    bindCartIcon();
    document.querySelectorAll('quantity-gifts').forEach((gifts) => { if (typeof gifts.update === 'function') gifts.update(); });
  });
  document.addEventListener('shopify:section:load', bindCartIcon);

})();
